<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { createServiceTicket } from '@/services/tickets'
import type { TicketSeverity } from '@/types/tickets'

const props = defineProps<{
  open: boolean
  review?: boolean
  plan?: { id: string; title: string }
  checklist?: { id: string; title: string; phase?: 'prep' | 'final' | 'post' }
}>()
const emit = defineEmits<{ close: []; created: [] }>()
const severity = ref<TicketSeverity>('medium')
const title = ref('')
const details = ref('')
const mistakes = ref('')
const whatWentWell = ref('')
const saving = ref(false)
const error = ref('')
const isReview = computed(() => props.review === true)
const canSubmit = computed(() => isReview.value || Boolean(title.value.trim() && details.value.trim()))

function reset() {
  severity.value = 'medium'
  title.value = isReview.value && props.plan ? `${props.plan.title} service review` : ''
  details.value = ''
  mistakes.value = ''
  whatWentWell.value = ''
  error.value = ''
}

watch(() => props.open, open => { if (open) reset() })

async function submit() {
  if (!canSubmit.value) return
  saving.value = true
  error.value = ''
  try {
    await createServiceTicket({
      type:isReview.value ? 'service-review' : 'issue', severity:severity.value, title:title.value.trim() || undefined, details:details.value.trim() || undefined,
      mistakes:mistakes.value.trim() || undefined, whatWentWell:whatWentWell.value.trim() || undefined,
      planId:props.plan?.id, checklistId:props.checklist?.id, checklistTitle:props.checklist?.title, checklistPhase:props.checklist?.phase
    })
    emit('created')
    emit('close')
  } catch {
    error.value = 'Unable to save this report. Please try again.'
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="ticket-dialog-backdrop" role="presentation" @click.self="emit('close')">
      <section class="ticket-dialog panel" role="dialog" aria-modal="true" :aria-label="isReview ? 'Service review' : 'Report an issue'">
        <header><p class="eyebrow">{{ isReview ? 'SERVICE REVIEW' : 'ISSUE TICKET' }}</p><h2>{{ isReview ? 'How did the service go?' : 'Report an issue' }}</h2><p>{{ isReview ? 'Capture what needs attention and what helped the team succeed. This is shared with the AV team to improve future services.' : 'Log an issue during prep, service, or post-service so the team can track it through resolution.' }}</p></header>
        <p v-if="plan" class="ticket-context">{{ plan.title }}<template v-if="checklist"> · {{ checklist.title }}</template></p>
        <form @submit.prevent="submit">
          <label v-if="!isReview">Issue title<input v-model="title" required placeholder="What needs attention?" /></label>
          <label>Severity<select v-model="severity"><option value="low">Low — minor follow-up</option><option value="medium">Medium — needs attention</option><option value="high">High — affects service readiness</option><option value="critical">Critical — immediate follow-up</option></select></label>
          <label>{{ isReview ? 'Issues or service notes' : 'What happened?' }}<textarea v-model="details" :required="!isReview" rows="4" :placeholder="isReview ? 'Anything that needs follow-up, context, or a process improvement?' : 'Include the system, symptoms, timing, and any attempted fix.'" /></label>
          <label v-if="isReview">Mistakes or process gaps<textarea v-model="mistakes" rows="3" placeholder="What could we do differently next time?" /></label>
          <label v-if="isReview">What went well<textarea v-model="whatWentWell" rows="3" placeholder="Call out workflows, people, or systems that helped." /></label>
          <p v-if="error" class="error">{{ error }}</p>
          <footer><button type="button" class="button" @click="emit('close')">{{ isReview ? 'Skip for now' : 'Cancel' }}</button><button class="button primary" :disabled="saving || !canSubmit">{{ saving ? 'Saving…' : isReview ? 'Submit review' : 'Create ticket' }}</button></footer>
        </form>
      </section>
    </div>
  </Teleport>
</template>
